import React, { useCallback, useEffect, useRef, useState } from "react";
import AuthImg from "@/components/AuthImg";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import {
  MessageCircle, X, ChevronLeft, Send, Loader2,
} from "lucide-react";

const CONTACTS_POLL_MS = 12_000;
const CONVO_POLL_MS = 4_000;

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  } catch (_e) { return ""; }
}

function OnlineDot({ online, className }) {
  return (
    <span className={cn("relative inline-flex h-2.5 w-2.5", className)}>
      {online && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />}
      <span className={cn("relative inline-flex h-2.5 w-2.5 rounded-full ring-2 ring-white", online ? "bg-emerald-500" : "bg-zinc-300")} />
    </span>
  );
}

function Avatar({ contact, token, size = "h-9 w-9" }) {
  return (
    <div className={cn(size, "relative rounded-full overflow-hidden bg-zinc-100 flex items-center justify-center text-xs font-semibold text-zinc-600 shrink-0")}>
      {contact.avatar_path
        ? <AuthImg path={contact.avatar_path} token={token} className="h-full w-full object-cover" alt={contact.name} />
        : (contact.name || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

/**
 * Team-Chat-Widget (unten rechts) — nur Admin + Buchhalter.
 * Direkter 1:1-Messenger mit Live-Online-Status.
 */
export function TeamChatWidget() {
  const { user, accessToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [anyOnline, setAnyOnline] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [active, setActive] = useState(null); // Kontakt-Objekt
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const eligible = user && (user.role === "admin" || user.role === "backoffice");

  const loadContacts = useCallback(async () => {
    try {
      const { data } = await api.get("/team-chat/contacts");
      setContacts(data.contacts || []);
      setAnyOnline(!!data.any_online);
      // Wenn eine Konversation offen ist, zählt deren unread nicht (wird beim Abruf gelesen)
      const act = activeRef.current;
      setTotalUnread((data.contacts || [])
        .filter((c) => !(act && c.id === act.id))
        .reduce((s, c) => s + (c.unread || 0), 0));
    } catch (_e) { /* silent */ }
  }, []);

  const loadConversation = useCallback(async (contact) => {
    try {
      const { data } = await api.get(`/team-chat/with/${contact.id}`);
      setMessages(data.messages || []);
    } catch (_e) { /* silent */ }
  }, []);

  // Polling: Kontakte/Unread immer (wenn berechtigt)
  useEffect(() => {
    if (!eligible) return undefined;
    loadContacts();
    const i = setInterval(loadContacts, CONTACTS_POLL_MS);
    return () => clearInterval(i);
  }, [eligible, loadContacts]);

  // Polling: offene Konversation
  useEffect(() => {
    if (!eligible || !active) return undefined;
    loadConversation(active);
    const i = setInterval(() => loadConversation(active), CONVO_POLL_MS);
    return () => clearInterval(i);
  }, [eligible, active, loadConversation]);

  // Auto-Scroll ans Ende
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, active, open]);

  if (!eligible) return null;

  const send = async () => {
    const body = draft.trim();
    if (!body || !active || sending) return;
    setSending(true);
    setDraft("");
    // Optimistic
    const tmp = {
      id: `tmp-${Date.now()}`, sender_id: user.id, recipient_id: active.id,
      body, created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tmp]);
    try {
      const { data } = await api.post(`/team-chat/with/${active.id}`, { body });
      setMessages((m) => m.map((x) => (x.id === tmp.id ? data.message : x)));
    } catch (_e) {
      setMessages((m) => m.filter((x) => x.id !== tmp.id));
      setDraft(body);
    } finally {
      setSending(false);
    }
  };

  const openContact = (c) => {
    setActive(c);
    setMessages([]);
    setContacts((list) => list.map((x) => (x.id === c.id ? { ...x, unread: 0 } : x)));
  };

  return (
    <>
      {/* Floating Bubble */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-[60] h-14 w-14 rounded-full shadow-xl",
          "bg-gradient-to-br from-sky-500 to-violet-600 text-white flex items-center justify-center",
          "transition-transform hover:scale-105 active:scale-95",
        )}
        data-testid="team-chat-bubble"
        title="Team-Chat (Admin ↔ Buchhaltung)"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {totalUnread > 0 && !open && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white"
                data-testid="team-chat-unread-badge">
            {totalUnread > 9 ? "9+" : totalUnread}
          </span>
        )}
        {anyOnline && !open && (
          <span className="absolute bottom-0.5 right-0.5" data-testid="team-chat-online-dot">
            <OnlineDot online />
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-36 lg:bottom-24 right-4 lg:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[360px] h-[480px] max-h-[70vh] rounded-2xl border border-zinc-200 bg-white shadow-2xl flex flex-col overflow-hidden"
          data-testid="team-chat-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-sky-500 to-violet-600 text-white px-4 py-3 flex items-center gap-2 shrink-0">
            {active ? (
              <>
                <button type="button" onClick={() => { setActive(null); loadContacts(); }}
                        className="hover:bg-white/15 rounded-md p-1 -ml-1" data-testid="team-chat-back">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <Avatar contact={active} token={accessToken} size="h-8 w-8" />
                <div className="min-w-0">
                  <div className="text-sm font-bold truncate">{active.name}</div>
                  <div className="text-[10px] opacity-90 flex items-center gap-1">
                    <OnlineDot online={active.is_online} />
                    {active.is_online ? "Online" : "Offline"}
                  </div>
                </div>
              </>
            ) : (
              <>
                <MessageCircle className="h-4 w-4" />
                <div className="text-sm font-bold">Team-Chat</div>
                <div className="text-[10px] opacity-80 ml-1">Admin ↔ Buchhaltung</div>
              </>
            )}
          </div>

          {/* Body */}
          {!active ? (
            <div className="flex-1 overflow-y-auto" data-testid="team-chat-contacts">
              {contacts.length === 0 ? (
                <div className="text-center text-sm text-zinc-400 py-10 px-4">
                  Keine Chat-Partner gefunden.
                </div>
              ) : contacts.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => openContact(c)}
                  className="w-full text-left px-4 py-3 border-b border-zinc-100 last:border-0 flex items-center gap-3 hover:bg-zinc-50 transition"
                  data-testid={`team-chat-contact-${c.email}`}
                >
                  <div className="relative">
                    <Avatar contact={c} token={accessToken} />
                    <span className="absolute -bottom-0.5 -right-0.5">
                      <OnlineDot online={c.is_online} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-zinc-900 truncate">{c.name}</span>
                      <span className={cn("text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded",
                        c.role === "admin" ? "bg-rose-100 text-rose-700" : "bg-sky-100 text-sky-700")}>
                        {c.role === "admin" ? "Admin" : "Buchhalter"}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500 truncate">
                      {c.last_message || (c.is_online ? "Jetzt online" : "Noch keine Nachrichten")}
                    </div>
                  </div>
                  {c.unread > 0 && (
                    <span className="min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                      {c.unread}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-zinc-50/60" data-testid="team-chat-messages">
                {messages.length === 0 && (
                  <div className="text-center text-xs text-zinc-400 pt-8">
                    Noch keine Nachrichten — schreib die erste! 👋
                  </div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                      <div className={cn(
                        "max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words",
                        mine
                          ? "bg-gradient-to-r from-sky-500 to-violet-600 text-white rounded-br-md"
                          : "bg-white border border-zinc-200 text-zinc-800 rounded-bl-md",
                      )}>
                        {m.body}
                        <div className={cn("text-[9px] mt-0.5 text-right", mine ? "text-white/70" : "text-zinc-400")}>
                          {fmtTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-zinc-200 p-2.5 flex items-center gap-2 shrink-0 bg-white">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Nachricht schreiben…"
                  className="flex-1 h-10 rounded-xl border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                  data-testid="team-chat-input"
                />
                <button
                  type="button"
                  onClick={send}
                  disabled={!draft.trim() || sending}
                  className="h-10 w-10 rounded-xl bg-gradient-to-r from-sky-500 to-violet-600 text-white flex items-center justify-center disabled:opacity-40 transition"
                  data-testid="team-chat-send"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
